import React, { useState, useEffect } from 'react';
import Zoom from 'react-medium-image-zoom';

const title = "New Business Setup";
const description = `To set up your new business on the system, 
we'll need to complete a few key steps. First, we'll create 
your company profile, which includes all your basic business information.
 Next, we'll handle the Advanced Options Setup to configure settings 
 specific to your business operations. Then, we'll create your
  Chart of Accounts (COA), which is the foundational list of all your 
  financial accounts. After that, we will add your Opening Balances to 
  accurately reflect your business's starting financial position. Finally,
   we'll import your products into the system, so you can start managing
    your inventory and sales right away.`;

const images = [
    '/images/settings/advanced-options/advanced-options.png', // 0
    '/images/settings/advanced-options/discount-level-options.png', // 1
    '/images/settings/advanced-options/creation-control-options.png', // 2
    '/images/settings/advanced-options/profit-margin-display.png', // 3
    '/images/settings/advanced-options/form-control.png', // 4
    '/images/settings/advanced-options/other-options.png', // 5
];

const topic = [
    {
        title: 'Home',
        description: ``,
        id: 'home',
    },
    {
        title: 'Profile',
        description: `.`,
        id: 'profile',
    },
    {
        title: 'Advanced Options',
        description: `.`,
        id: 'advanced-options',
    },
    {
        title: 'Profit Margin Display',
        description: `.`,
        id: 'profit-margin-display',
    },
    {
        title: 'Form Options',
        description: `.`,
        id: 'form-options',
    },
    {
        title: 'Other Options',
        description: `.`,
        id: 'other-options',
    },

];

const NewBusiness: React.FC = () => {
    const [activeId, setActiveId] = useState(topic[0].id);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const newActiveId = entry.target.id;
                        setActiveId(newActiveId);
                        window.history.replaceState(null, '', `#${newActiveId}`);
                    }
                });
            },
            {
                root: null,
                rootMargin: '-50% 0% -50% 0%',
                threshold: 0,
            }
        );

        topic.forEach((item) => {
            const section = document.getElementById(item.id);
            if (section) {
                observer.observe(section);
            }
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    const handleItemClick = (id: string) => {
        setActiveId(id);
    };

    return (
        <div className="max-w-4xl min-h-screen bg-gray-50 flex gap-2">
            <div className="overflow-auto mx-auto flex-2">
                {/* Chart of Accounts */}
                <div className="mb-6"   id={topic[0].id}>
                    <h1 className="text-3xl font-bold pt-4 text-gray-900">
                        {title}
                    </h1>
                    <p className="text-gray-600 mt-4">
                        {description}
                    </p>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 my-6">
                        <p className="text-gray-600 my-2">
                            In Advanced Options, you can use the checkboxes and on/off switches to configure and control various system settings.
                        </p>
                        <Zoom>
                            <img
                                src={images[0]}
                                alt={title}
                                className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                        </Zoom>
                    </div>
                </div>

                {/* Discount Level Options */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"  id={topic[1].id}>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{topic[1].title}</h2>
                    <p className="text-gray-600 mb-4">
                        Choose between a fixed amount or a percentage for your discount. Then, decide if the discount applies to a single item or the entire transaction.
                    </p>
                    <img
                        src={images[1]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>

                </div>

                {/* Creation Control Options */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"  id={topic[2].id}>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{topic[2].title}</h2>
                    <p className="text-gray-600 mb-4">
                        With the Creation Control Options, you can set rules for creating bills and invoices. Checking these boxes
                        ensures that bills can only be made from purchase orders and invoices can only be made from sales orders.
                    </p>
                    <img
                        src={images[2]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>

                </div>

                {/* Profit Margin Display */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"  id={topic[3].id}>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{topic[3].title}</h2>
                    <p className="text-gray-600 mb-4">
                        You can choose to display the profit margin as either a fixed amount or a percentage.
                    </p>
                    <img
                        src={images[3]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>

                </div>

                {/* Form Options */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"  id={topic[4].id}>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{topic[4].title}</h2>
                    <p className="text-gray-600 mb-4">
                        With these three options, you can control what can be changed on a form.
                    </p>
                    <ul className={`p-4 list-disc`}>
                        <li><b>Allow Amount Adjustment</b> lets you manually change the total amount on a form.</li>
                        <li><b>Allow Custom Line Item</b> gives you the ability to add new, custom items that are not in your regular product list.</li>
                        <li><b>Allow Edit After Confirmed</b> lets you make changes to a form even after it has been finalized.</li>
                    </ul>
                    <img
                        src={images[4]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>

                </div>

                {/* Form Options */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"  id={topic[4].id}>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{topic[4].title}</h2>
                    <p className="text-gray-600 mb-4">
                        With these three options, you can control what can be changed on a form.
                    </p>
                    <ul className={`p-4 list-disc`}>
                        <li><b>Allow Amount Adjustment</b> lets you manually change the total amount on a form.</li>
                        <li><b>Allow Custom Line Item</b> gives you the ability to add new, custom items that are not in your regular product list.</li>
                        <li><b>Allow Edit After Confirmed</b> lets you make changes to a form even after it has been finalized.</li>
                    </ul>
                    <img
                        src={images[4]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>

                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"  id={topic[5].id}>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{topic[5].title}</h2>
                    <p className="text-gray-600 mb-4">
                        In Other Options, you can set a few helpful automatic features for your system.
                    </p>
                    <ul className={`p-4 list-disc`}>
                        <li><b>Activate Batch Tracking</b> lets you track products by specific groups or batches, which
                            is helpful for managing inventory and returns.</li>
                        <li><b>Activate Product SKU Auto Generate</b> automatically creates a unique code for each
                            new product you add, saving you time.</li>
                        <li><b>Activate Batch Number Auto Generate</b> automatically creates a unique number for each
                            product batch, making it easier to track items.</li>
                        <li><b>Include Signature on Purchase Order and Include Signature on Bill</b>add a signature line
                            to the printed versions of these documents for formal approval.</li>
                    </ul>
                        <Zoom>
                            <img
                                src={images[5]}
                                alt={title}
                                className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                        </Zoom>

                </div>
            </div>

            {/* side route */}
            <div className="fixed w-1/5 right-2 max-2xl:hidden">
                <ul>
                    {topic.map((item) => (
                        <li key={item.id} className="mb-4">
                              <span className="text-xs">
                                    <a
                                        className={`hover:text-blue-700 border-l p-4 ${
                                            activeId === item.id ? 'text-blue-700 font-bold border-blue-700' : ''
                                        }`}
                                        href={`#${item.id}`}
                                        onClick={() => handleItemClick(item.id)}
                                    >
                                      {item.title}
                                    </a>
                              </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default NewBusiness;
